import { getPeriodRange, LayoutHeader, LayoutPage, PeriodValue } from '@shinetools/lumen-react'
import debounce from 'lodash/debounce'
import { ReactElement, useMemo, useState } from 'react'

import { BillsTable } from '@views/bills/elements/BillsTable'
import { defaultBillsTableColumnConfiguration } from '@views/bills/elements/BillsTable/constants/billsTableColumnConfigurations'
import { BillsTableSortCriterion } from '@views/bills/elements/BillsTable/types/billsTableSortCriterion'
import { formatFetchBillsResponseToBillsTableData } from '@views/bills/elements/BillsTable/utils/formatFetchBillsResponseToBillsTableData'

import { BillsListHeaderActions } from './elements/BillsListHeaderActions'
import { useFetchBills } from './hooks/useFetchBills'
import { transformBillsTableSortToBillsApiSort } from './utils/transformBillsTableSortToBillsApiSort'

const SEARCH_MINIMUM_CHARACTER_LENGTH = 3
const SEARCH_DEBOUNCE_DELAY_MS = 250
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

interface BillsListRevampedProps {
  organizationId: string
  isLoading?: boolean
}

/**
 * @todo Handle localization
 * @see https://linear.app/shine-co/issue/EXP-357/web-add-localization
 */
export const BillsListRevamped = ({ organizationId, isLoading }: BillsListRevampedProps): ReactElement => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortCriteria, setSortCriteria] = useState<BillsTableSortCriterion | null>(null)

  // `null` means "All" – no receipt date filtering
  const [period, setPeriod] = useState<PeriodValue | null>(null)
  const periodRange = useMemo(() => (period ? getPeriodRange(period) : null), [period])

  const response = useFetchBills({
    organizationId,
    searchQuery: debouncedSearchValue.length >= SEARCH_MINIMUM_CHARACTER_LENGTH ? debouncedSearchValue : undefined,
    pagination: {
      page,
      pageSize,
    },
    filters: {
      minReceiptDate: periodRange?.start,
      maxReceiptDate: periodRange?.end,
    },
    sorting: transformBillsTableSortToBillsApiSort(sortCriteria),
  })

  const data = formatFetchBillsResponseToBillsTableData(response)

  const updateDebouncedSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value)
      }, SEARCH_DEBOUNCE_DELAY_MS),
    [],
  )

  const handleSearchChange = (newSearchValue: string) => {
    setSearchValue(newSearchValue)
    updateDebouncedSearchValue(newSearchValue)
  }

  return (
    <LayoutPage layout="list-table" header={<LayoutHeader title="Purchases" actions={<BillsListHeaderActions />} />}>
      <BillsTable
        isLoading={response.isLoading}
        selectable
        data={data}
        columns={defaultBillsTableColumnConfiguration}
        totalCount={response.pagination?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={handleSearchChange}
        onSortCriteriaChange={setSortCriteria}
        searchValue={searchValue}
        sortCriteria={sortCriteria}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        period={period}
        onPeriodChange={setPeriod}
      />
    </LayoutPage>
  )
}
